// The member and registration pages' English (registration rebuild): sign-in
// and account, My account, password recovery, Register, the members-only
// wall and the invitation / team-code join page. Moved here from
// site/core/labels/ui.ts (#1448, #1464); ui.ts reads them as ui.<group>.
// fr.ts / es.ts mirror them. Decision 23 holds in every language: plain
// professional wording, no durations.

const en = {
  account: {
    signIn: 'Sign in',
    createAccount: 'Create an account',
    introSignIn: (league: string): string => `Sign in to your ${league} account.`,
    introSignUp: (league: string): string => `Create your ${league} account.`,
    toSignUpPrompt: 'New here?',
    toSignUpAction: 'Create an account',
    toSignInPrompt: 'Already have an account?',
    toSignInAction: 'Sign in',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    password: 'Password',
    passwordHelp: 'At least 8 characters.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    submitSignIn: 'Sign in',
    busySignIn: 'Signing in…',
    submitSignUp: 'Create account',
    busySignUp: 'Creating account…',
    /** Header account control. */
    signOut: 'Sign out',
    /** The header menu's link to /account (plan step 9). */
    myAccount: 'My account',
    /** The header label while the member's name is not known yet. */
    fallbackName: 'Account',
    menuLabel: (name: string): string => `Account menu, signed in as ${name}`,
    /** The menu trigger's name while the member's name is not known. */
    menuLabelUnnamed: 'Account menu',
    signedInAs: (name: string): string => `Signed in as ${name}`,
    /** Errors. Fixed copy: never the raw error (#420), except the server
     *  messages written for display (validation_failed, account_exists). */
    incorrect: 'Incorrect email or password.',
    accountExists: 'An account with this email already exists. Sign in instead.',
    tooMany: 'Too many attempts. Wait a minute, then try again.',
    /** Sign-in's 429 too_many_attempts: five wrong tries on one email (owner, October 30). */
    lockedOut: 'Too many attempts. Try again in 15 minutes, or reset your password.',
    unavailable: "We can't sign you in right now. Try again in a moment.",
    offline: "You're offline. Check your connection and try again.",
    failed: 'Something went wrong. Try again.',
    blankFirstName: 'Enter your first name.',
    blankLastName: 'Enter your last name.',
    blankEmail: 'Enter your email address.',
    blankPassword: 'Enter your password.',
    passwordTooShort: 'Password must be at least 8 characters.',
    /** Under the password on every member sign-in surface (plan step 9). */
    forgotPassword: 'Forgot password?',
    /** Email verification with a code (October 29). The server's own
     *  message ("We sent a 6-digit code to …") is the notice. */
    verifyTitle: 'Check your email',
    verifyIntro: 'Enter the 6-digit code we emailed you.',
    code: 'Code',
    submitVerify: 'Verify email',
    busyVerify: 'Verifying…',
    resendPrompt: "Didn't get the email?",
    resendAction: 'Send a new code',
    /** While the 30-second resend wait runs after a code went out (QA, October 30). */
    resendIn: (seconds: number): string => `Send a new code in ${seconds} s`,
    codeResent: "We sent a new code. Check your inbox and spam folder.",
    blankCode: 'Enter the 6-digit code from the email.',
    alreadyVerified: 'Your email is already verified. Sign in instead.',
  },

  /** My account (/account, registration plan step 9, decision 23: plain
   *  professional wording, no durations anywhere). New page: no classic
   *  text. Server messages written for display (validation_failed on a
   *  field, no_profile, the photo's) are shown as they come. */
  myAccount: {
    title: 'My account',
    intro: (league: string): string => `Manage your ${league} account.`,
    /** Your details. */
    detailsHeading: 'Your details',
    email: 'Email',
    emailHelp: "Your email can't be changed here.",
    phone: 'Phone',
    dateOfBirth: 'Date of birth',
    optional: 'Optional',
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Your details have been saved.',
    noProfile: 'Your account has no player profile yet. Contact the league.',
    saveUnavailable: "We can't save your changes right now. Try again in a moment.",
    /** Photo. */
    photoHeading: 'Photo',
    addPhoto: 'Add photo',
    changePhoto: 'Change photo',
    uploading: 'Uploading…',
    photoSaved: 'Your photo has been updated.',
    photoHelp: 'JPG, PNG or WebP, up to 20 MB.',
    photoTooLarge: 'Choose a photo under 20 MB.',
    photoBusy: "We're processing other photos. Try again in a moment.",
    photoUnavailable: "We can't upload your photo right now. Try again in a moment.",
    photoAlt: (name: string): string => `Photo of ${name}`,
    /** Password. */
    passwordHeading: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    passwordHelp: 'At least 8 characters.',
    change: 'Change password',
    changing: 'Changing password…',
    changed: "Your password has been changed. You've been signed out on your other devices.",
    blankCurrent: 'Enter your current password.',
    blankNew: 'Enter a new password.',
    /** Pending invitations (owner, October 30): a lost email is no dead end. */
    invitationsHeading: 'Your invitations',
    openInvitation: 'View invitation',
    /** My registrations. */
    registrationsHeading: 'My registrations',
    registrationsEmpty: "You haven't registered for anything yet.",
    browseRegistrations: 'See open registrations',
    entriesFailed: "We couldn't load your registrations. Try again.",
    retry: 'Try again',
    signedUp: (d: string): string => `Signed up ${d}`,
    viewRegistration: 'View registration',
    cancelledByLeague: 'Cancelled by the league',
    loadFailed: "We can't load your account right now. Try again in a moment.",
    /** Paying for a pending entry (step 12). */
    totalDue: (amount: string): string => `Total due ${amount}`,
    payNow: 'Pay now',
    openingCheckout: 'Opening checkout…',
    /** Step 23: a player's own fee on a team registration where each player pays. */
    payYourShare: 'Pay your share',
    /** Step 23: a team registration whose captain pays one team fee. */
    captainPaysTeamFee: 'Your captain pays the team fee.',
    /** The member's team on a registration (October 28, owner). */
    teamLine: (name: string | null): string => (name ? `Team: ${name}` : 'Team: not named yet'),
    /** Step 35: signing a waiver later. */
    signWaiver: 'Sign the waiver',
    waiverNotNow: 'Not now',
    loadWaiverFailed: "We couldn't load the waiver. Try again.",
    /** Step 38: the waitlist (no durations; only the expiry date). */
    onWaitlist: 'On the waitlist',
    spotOffered: 'Spot offered',
    offerExpiredStatus: 'Offer expired',
    positionNote: (n: number): string => `Number ${n} in line.`,
    acceptByNote: (date: string): string => `A spot is open for you. Accept by ${date}.`,
    offerExpiredNote: 'Your offer for a spot expired.',
    acceptSpot: 'Accept your spot',
    accepting: 'Accepting…',
    acceptedSpot: "You've accepted your spot.",
    leaveWaitlist: 'Leave the waitlist',
    leftWaitlist: "You've left the waitlist.",
  },

  /** Password recovery on the league site (/forgot-password and
   *  /reset-password, registration plan step 9). The staff pages' pattern
   *  and wording, decision 23: no durations anywhere. */
  recovery: {
    forgotTitle: 'Reset your password',
    forgotIntro: "Enter the email address for your account and we'll send you a link to reset your password.",
    sendLink: 'Send reset link',
    sending: 'Sending…',
    sentTitle: 'Check your email',
    sentBody: (email: string): string =>
      `If an account exists for ${email}, you'll receive an email with a link to reset your password.`,
    resendPrompt: "Didn't receive it? Check your spam folder or",
    resendAction: 'send it again',
    resent: 'Email sent again.',
    backToSignIn: 'Back to sign in',
    sendUnavailable: "We can't send a reset link right now. Try again in a moment.",
    resetTitle: 'Choose a new password',
    confirmPassword: 'Confirm new password',
    resetSubmit: 'Reset password',
    resetting: 'Resetting password…',
    checkingLink: 'Checking your link…',
    checkFailed: "We couldn't check your link. Try again.",
    mismatch: "The passwords don't match.",
    resetUnavailable: "We can't reset your password right now. Try again in a moment.",
    expiredTitle: 'This link has expired',
    expiredBody: 'Request a new link to reset your password.',
    requestNew: 'Request new link',
    doneTitle: 'Your password has been reset',
    doneBody: 'Sign in with your new password.',
    signIn: 'Sign in',
  },

  /** The public Register pages (registration plan step 6, decision 23:
   *  plain professional wording, no durations). New pages: no classic text.
   *  Server messages written for display (sign_up.message, the 409 codes,
   *  validation_failed) are shown as they come; everything else is here. */
  registration: {
    /** The site menu item (every template, only with the add-on). */
    navLabel: 'Register',
    listTitle: 'Register',
    listIntro: (league: string): string => `Sign up to play with ${league}.`,
    /** The member pages (/register, /register/:slug, /register/join,
     *  /account) while the league's registration add-on is off (owner
     *  audit, October 29): a plain page instead of not found, for a
     *  Stripe return or an emailed link that arrives then. */
    offTitle: "Registration isn't available right now",
    offBody: (league: string): string => `Contact ${league}.`,
    emptyTitle: 'No registrations are open right now.',
    emptyBody: 'Check back soon. New registrations appear here as soon as they open.',
    free: 'Free',
    opens: (d: string): string => `Opens ${d}`,
    /** Huddle's home sign-up card: an upcoming registration's tag. */
    registrationOpens: (d: string): string => `Registration opens ${d}`,
    /** A registration card's action link, named with its registration (Huddle). */
    cardActionLabel: (action: string, name: string): string => `${action}: ${name}`,
    closes: (d: string): string => `Closes ${d}`,
    spotsLeft: (n: number): string => (n === 1 ? '1 spot left' : `${n} spots left`),
    full: 'Full',
    viewAction: 'View and register',
    viewDetails: 'View details',
    allRegistrations: 'All registrations',
    /** The facts block. */
    factSeason: 'Season',
    factPrice: 'Price',
    factOpens: 'Opens',
    factCloses: 'Closes',
    factSpots: 'Spots',
    /** The sign-in / create-account panel on the registration page. */
    panelSignUpTitle: 'Create an account to register',
    panelSignInTitle: 'Sign in to register',
    panelIntro: (league: string): string => `You'll use this account to manage your registration with ${league}.`,
    sessionEnded: 'Sign in to continue your registration.',
    /** The questions form. */
    formHeading: 'Your details',
    registeringAs: (name: string): string => `Registering as ${name}`,
    notYou: 'Not you? Sign out',
    phone: 'Phone',
    phoneHelp: 'The league may use this to contact you about this registration.',
    optional: 'Optional',
    questionsHeading: 'Questions',
    choose: 'Choose…',
    submit: 'Register',
    busy: 'Registering…',
    summaryTitle: 'Check your answers and try again.',
    /** A required question left empty, or a required checkbox left unticked,
     *  caught before anything is sent (the server's own wording). */
    answerRequired: (label: string): string => `${label} is required.`,
    /** Already registered. */
    registeredTitle: "You're registered",
    registeredOn: (d: string): string => `You registered on ${d}.`,
    contactLeague: (league: string): string => `To make changes, contact ${league}.`,
    /** Cancelling. */
    cancelAction: 'Cancel registration',
    cancelConfirmTitle: (name: string): string => `Cancel your registration for ${name}?`,
    cancelConfirmBody: "You'll give up your spot. You can register again while registration is open and spots remain.",
    /** An invitation-only registration: the invitation is used, so coming back takes a new one. */
    cancelConfirmBodyInviteOnly: "You'll give up your spot. To register again, you'll need a new invitation from the league.",
    /** A sign-up that hasn't paid holds no spot, so there's none to give up. */
    cancelPendingBody: "You won't be charged. You can register again while registration is open and spots remain.",
    /** The same, when coming back takes a new invitation. */
    cancelPendingBodyInviteOnly: "You won't be charged. To register again, you'll need a new invitation from the league.",
    cancelConfirm: 'Cancel registration',
    keep: 'Keep my registration',
    cancelling: 'Cancelling…',
    cancelled: 'Your registration has been cancelled.',
    /** After a successful sign-up. */
    confirmedTitle: (name: string): string => `You're registered for ${name}.`,
    nextHeading: 'What happens next',
    nextLeague: (league: string): string => `${league} has your registration and will be in touch with team and schedule details.`,
    nextCancel: 'You can cancel here until registration closes.',
    backToList: 'See all registrations',
    /** Why the form isn't shown (the server's message is the body). */
    closedTitle: 'Registration is closed',
    notYetOpenTitle: "Registration hasn't opened yet",
    /** The not-yet-open body when the opening time is known (QA, October 30). */
    opensWhen: (when: string): string => `Registration opens ${when}. Come back then to register.`,
    fullTitle: 'This registration is full',
    notAvailableTitle: "Registration isn't available online",
    /** Errors. Fixed copy: never the raw error (#420). */
    loadFailed: "We couldn't load this registration. Try again.",
    listLoadFailed: "We couldn't load registrations. Try again.",
    offline: "You're offline. Check your connection and try again.",
    unavailableNow: "We can't complete your registration right now. Try again in a moment.",
    tooMany: 'Too many attempts. Wait a minute, then try again.',
    failed: 'Something went wrong. Try again.',
    /** Paying to register (step 12, decision 23: professional, no durations). */
    priceHeading: 'Payment summary',
    linePrice: 'Registration',
    lineServiceFee: 'Service fee',
    lineTotal: 'Total',
    priceNote: "You'll pay on a secure checkout page.",
    /** QA, October 30: first to pay gets the spot. */
    spotNotHeld: "Your spot isn't held until you pay.",
    continueToPayment: 'Continue to payment',
    openingCheckout: 'Opening checkout…',
    payTitle: 'Finish your payment',
    payBody: (name: string): string => `Complete your payment to finish registering for ${name}.`,
    payNow: 'Pay now',
    payNotCompletedTitle: 'Payment not completed',
    payCancelledBody: "Your payment wasn't completed, and you haven't been charged. You can pay now or come back later.",
    payExpiredBody: "Your checkout ended before the payment was completed. You haven't been charged.",
    confirmingTitle: 'Confirming your payment…',
    confirmingBody: 'Please keep this page open.',
    processingTitle: 'Your payment is being processed',
    processingBody: "You'll get an email when it's confirmed.",
    checkAgain: 'Check again',
    checking: 'Checking…',
    receiptHeading: 'Receipt',
    receiptAmount: 'Amount paid',
    receiptDate: 'Date',
    signInForPayment: 'Sign in to see your payment.',
    paymentNotApplied: (league: string): string =>
      `We received a payment that couldn't be applied to this registration. Contact ${league} about a refund.`,
    paymentRefunded: 'This payment was refunded.',
    /** Two payments at once: the second is refunded in full, automatically. */
    paymentSpotTaken: "The last spot was taken just before your payment came through, so it wasn't applied. It's being refunded in full.",
    checkoutFailed: "We couldn't start your payment. Try again.",
    paymentsUnavailableNow: "Payments aren't available right now. Try again later.",
    /** A player who joined a team waits for the league's approval (step 16;
     *  decision 15). Nothing here implies a place on a roster. */
    waitingTitle: 'Waiting for the league to approve',
    waitingBody: (league: string): string =>
      `${league} reviews each player who joins a team. Your registration is waiting for their approval.`,
    payWaitingBody: (name: string, league: string): string =>
      `Complete your payment for ${name}. ${league} then reviews your place on the team.`,
    paidWaitingTitle: 'Payment received',
    /** Step 21: registering a team from the page (a team registration with
     *  the public link on). The confirmation never says whether the address
     *  has an account (decision 19), and no copy states a duration. */
    registerTeamHeading: 'Register a team',
    registerTeamIntro: "Enter your team's name and your email. We'll email you a link to confirm you're the captain.",
    teamNameLabel: 'Team name',
    teamNameHelp: 'Players and the league will see this name.',
    yourEmailLabel: 'Your email',
    registerTeamSubmit: 'Register team',
    registerTeamBusy: 'Registering…',
    teamSentHeading: "Check your email to confirm you're the captain",
    /** October 29: a page team holds no place or name until confirmed, and says so. */
    teamSentBody: (email: string, team: string): string =>
      `We sent a link to ${email}. Open it to confirm ${team} and set up your sign-in. Your team isn't registered until you confirm.`,
    teamSentNote: "If it doesn't arrive, check your spam folder.",
    teamNameRequired: 'Enter a team name.',
    emailRequired: 'Enter your email address.',
    emailInvalid: 'Enter a valid email address.',
    /** Step 26: "Have a team code?" on a team registration's page. The
     *  code is checked for shape here, then the join page reads it. */
    teamCodeHeading: 'Have a team code?',
    teamCodeIntro: 'Enter the code your captain shared to join their team.',
    teamCodeLabel: 'Team code',
    teamCodeHelp: 'Six letters and numbers, like 7QH4KX. Spaces and dashes are fine.',
    teamCodeSubmit: 'Find team',
    teamCodeEmpty: 'Enter your team code.',
    teamCodeInvalid: 'Check the code. Team codes have six letters and numbers.',
    /** Step 35: the waiver step (a typed-name signature; plain text shown
     *  as text, never HTML). No durations; nothing implies a review of the
     *  signature itself. */
    waiverHeading: 'Waiver',
    waiverVersion: (n: number): string => `Version ${n}`,
    waiverIntro: 'Read the waiver, then type your full name to sign it.',
    waiverTextLabel: (title: string): string => `${title} text`,
    waiverNameLabel: 'Type your full name to sign',
    waiverNameHelp: 'Typing your name is your signature.',
    waiverChanged: 'The waiver has been updated. Read the new version, then sign it.',
    waiverAttachedNow: 'This registration now has a waiver. Read it, then sign it.',
    waiverDueTitle: 'Sign the waiver',
    waiverDueBodyPaid: 'Your payment is in. Sign the waiver to complete your registration.',
    waiverDueBodyApproval: "The league can approve you once you've signed.",
    waiverDueBody: 'Your registration is waiting for the waiver.',
    signWaiverSubmit: 'Sign the waiver',
    signingWaiver: 'Signing…',
    waiverSigned: "You've signed the waiver.",
    waiverBeforePay: 'Sign the waiver before you pay.',
    waiverNotNeeded: "This registration doesn't need a waiver any more.",
    /** PDF waivers (owner, October 17): open the PDF, tick, type the
     *  name. No durations. */
    waiverPdfIntro: 'Open the waiver and read it. Then tick the box and type your full name to sign it.',
    waiverPdfOpen: 'Open the waiver (PDF)',
    waiverPdfNewTab: '(opens in a new tab)',
    waiverPdfSize: (size: string): string => `PDF · ${size}`,
    waiverPdfHelp: 'On some phones the PDF downloads first. Open it from your downloads, then come back here to sign.',
    waiverPdfHint: 'Open the waiver to continue.',
    waiverPdfOpened: 'You opened the waiver.',
    waiverAgree: 'I have read and agree',
    waiverPdfUnavailable: "We can't open this waiver right now. Try again later or contact the league.",
    /** site/core/member/waiver.ts: caught before the request (the server's own wording). */
    waiverNameRequired: 'Type your full name to sign the waiver.',
    waiverNameTooLong: 'Your name must be 200 characters or less.',
    waiverConsentOpen: 'Open the waiver, then sign it.',
    waiverConsentAgree: 'Tick "I have read and agree" to sign the waiver.',
    /** Step 38: the waitlist (step 36). Nobody waiting is charged; no
     *  durations are stated: only the offer's expiry date is shown. */
    joinWaitlist: 'Join the waitlist',
    joiningWaitlist: 'Joining the waitlist…',
    waitlistFormNote: "This registration is full. Join the waitlist and we'll email you if a spot opens. Nobody is charged until a spot is offered to you.",
    waitlistedTitle: "You're on the waitlist",
    waitlistPosition: (n: number): string => `You're number ${n} in line.`,
    waitlistedBody: "We'll email you if a spot opens.",
    offerTitle: 'A spot is open for you',
    offerBody: (date: string): string => `Accept by ${date} to take it.`,
    offerPayNote: "After you accept, you'll pay to finish registering.",
    offerTeamNote: (league: string): string => `${league} reviews your place on the team after you accept.`,
    acceptSpot: 'Accept your spot',
    accepting: 'Accepting…',
    acceptedSpot: "You've accepted your spot.",
    offerExpiredTitle: 'This offer has expired',
    offerExpiredBody: 'The spot went to the next person on the waitlist.',
    offerExpired: 'This offer has expired.',
    offerLapsed: 'Your offer expired.',
    offerLapsedWaitlist: 'Your offer expired. Join the waitlist again below to get back in line.',
    offerLapsedOpen: 'Your offer expired, but you can still sign up below.',
    noOffer: "There's no spot to accept right now.",
    leaveWaitlist: 'Leave the waitlist',
    leaveConfirmTitle: (name: string): string => `Leave the waitlist for ${name}?`,
    leaveConfirmBody: "You'll lose your place in line.",
    keepPlace: 'Keep my place',
    leaving: 'Leaving…',
    leftWaitlist: "You've left the waitlist.",
    joinedWaitlist: "You've joined the waitlist.",
  },

  /** The invitation page (/register/join, registration plan step 16,
   *  decisions 19 and 23: never says whether an account exists, plain
   *  professional wording, no durations). Server messages written for
   *  display (sign_up.message, the 409 codes, validation_failed) are shown
   *  as they come; everything else is here. */
  /** The members-only sign-in wall (registration step 33): what a visitor
   *  who is not admitted sees on a private league's site. Plain words, no
   *  durations, "invitation" never "invite". Signed in as / Sign out reuse
   *  `account`. */
  membersOnly: {
    title: 'Members only',
    intro: (league: string): string => `${league}'s site is for registered players and league staff. Sign in to continue.`,
    sessionEnded: 'Your session has ended. Sign in again to continue.',
    notMember: (league: string): string =>
      `The staff account signed in on this browser isn't part of ${league}. Sign in with your player account to continue.`,
    finishTitle: 'Finish your registration',
    finishBody: (league: string): string =>
      `You're signed in, but your account doesn't have a completed registration with ${league} yet. Once you're registered, the site opens for you.`,
    finishRegister: 'See registrations',
    finishAccount: 'My account',
    finishAccountHelp: 'Started a registration already? Check its status or finish paying in My account.',
    closed: (league: string): string => `${league}'s site is for members only, and member sign-in isn't available right now.`,
    registerPrompt: 'Not registered yet?',
    registerAction: 'See open registrations',
    invitationNote: 'Received an invitation? Open the link in the email to join.',
    staffPrompt: 'League staff?',
    staffAction: 'Sign in to the admin',
    unavailable: "We couldn't check your access right now.",
    retry: 'Try again',
    loading: 'Checking your access…',
  },
  join: {
    eyebrow: 'Invitation',
    title: (team: string | null, registration: string, kind: 'player' | 'individual'): string =>
      kind === 'individual'
        ? `You're invited to register for ${registration}`
        : team
          ? `You're invited to join ${team}`
          : "You're invited to join a new team",
    invitedBy: (name: string): string => `Invited by ${name}`,
    sentTo: (email: string): string => `Sent to ${email}`,
    expires: (date: string): string => `This invitation expires on ${date}.`,
    forRegistration: (registration: string, season: string): string => `${registration} · ${season}`,
    /** Signed out: two choices side by side, neither preselected. */
    chooseIntro: 'Sign in or create an account to accept. Your registration is saved to that account.',
    chooseLabel: 'How do you want to continue?',
    chooseSignIn: 'Sign in',
    chooseSignUp: 'Create an account',
    emailHelp: 'Use the email address this invitation was sent to.',
    /** An invitation is locked to the invited email (owner, October 30). */
    wrongEmail: (masked: string): string => `This invitation was sent to ${masked}. Sign in with that email to accept it.`,
    /** Season import: an invitation to finish a place the league already made. */
    finishTitle: (team: string | null): string => (team ? `Finish your registration with ${team}` : 'Finish your registration'),
    finishEmailHelp: (email: string): string => `Use ${email} so your place on the team is kept.`,
    submitSignUp: 'Create account and accept',
    busySignUp: 'Creating your account…',
    submitSignIn: 'Sign in and accept',
    busySignIn: 'Signing in…',
    accept: 'Accept invitation',
    accepting: 'Accepting…',
    /** Signed in. */
    signedInAs: (email: string): string => `Signed in as ${email}`,
    notYou: 'Not you? Sign out',
    /** The sign-up's 409 account_exists, said neutrally (quirk 22: no automatic switch). */
    accountExists: "We couldn't create an account with that email. If you already have one, sign in to accept your invitation.",
    signInInstead: 'Sign in instead',
    sessionEnded: 'Sign in to continue.',
    questionsHeading: 'Questions',
    /** Dead ends. notFound also covers a refresh (the token is gone) and another league's link. */
    notFoundTitle: "This invitation link isn't valid",
    notFoundBody: "Open the full link from your invitation email. If it still doesn't work, ask the person who invited you to send a new one.",
    usedTitle: 'This invitation has already been used',
    usedBody: 'If you accepted it, sign in to see your registration.',
    /** Signed in, the link goes to My account (the 410 names no registration). */
    usedBodySignedIn: 'If you accepted it, your registration is in My account.',
    signIn: 'Sign in',
    revokedTitle: 'This invitation is no longer valid',
    revokedBody: 'Ask the person who invited you to send a new one.',
    expiredTitle: 'This invitation has expired',
    expiredBody: 'Ask the person who invited you to send a new one.',
    /** Why the invitation can't be accepted now (the server's message is the body). */
    closedTitle: 'Registration is closed',
    notYetOpenTitle: "Registration hasn't opened yet",
    fullTitle: 'This registration is full',
    rosterFullTitle: 'This team is full',
    teamNotActiveTitle: "This team isn't taking part",
    notAvailableTitle: "Registration isn't available online",
    unavailableTitle: "You can't accept this invitation right now",
    viewRegistration: 'View registration',
    seeRegistrations: 'See open registrations',
    loadingLabel: 'Opening your invitation…',
    loadFailed: "We couldn't open this invitation. Try again.",
    /** Step 26: the same page opened with a team code (?team_code=). Fixed
     *  copy for every refusal: the server's messages are never shown here. */
    codeEyebrow: 'Team code',
    codeTitle: (team: string | null, captain: string | null): string =>
      team ? `Join ${team}` : captain ? `Join ${captain}'s team` : 'Join this team',
    captainLine: (name: string): string => `Captain: ${name}`,
    spotsLeft: (n: number): string => (n === 1 ? '1 spot left on the roster' : `${n} spots left on the roster`),
    captainPays: 'Your captain pays the team fee.',
    payShare: "You pay your share after you join. Your spot isn't held until you pay.",
    nothingToPay: "There's nothing to pay to join this team.",
    codeChooseIntro: 'Sign in or create an account to join the team. Your registration is saved to that account.',
    submitSignUpCode: 'Create account and join',
    /** Email verification (October 29): the code, then the invitation or the team. */
    submitVerify: 'Verify and accept',
    submitVerifyCode: 'Verify and join',
    submitSignInCode: 'Sign in and join',
    joinTeam: 'Join team',
    joining: 'Joining…',
    codeAccountExists: "We couldn't create an account with that email. If you already have one, sign in to join the team.",
    codeNoProfile: 'Your account has no player profile yet. Contact the league.',
    /** QA C9: 409 already_on_other_team without the server's sentence (which names the team). */
    alreadyOnOtherTeam: "You're already on another team in this registration. Ask the league if you need to move.",
    codeNotFoundTitle: "We couldn't find that team code",
    codeNotFoundBody: 'Check the code with your captain and try again. A code stops working when the captain replaces it.',
    codeEntryLabel: 'Team code',
    codeEntryHelp: 'Six letters and numbers, like 7QH4KX. Spaces and dashes are fine.',
    codeEntryBlank: 'Enter your team code.',
    codeEntryInvalid: 'Check the code. Team codes have six letters and numbers.',
    findTeam: 'Find team',
    codeTooMany: 'Too many incorrect team codes. Try again later.',
    codeLoadingLabel: 'Finding the team…',
    codeUnavailableTitle: "You can't join this team right now",
    codeClosedBody: 'Sign-up for this registration has closed.',
    codeNotOpenBody: "Sign-up hasn't opened yet. Your team code works once it opens.",
    codeRosterFullBody: "This team's roster is full. Check with your captain.",
    codeTeamNotActiveBody: 'This team is no longer taking part.',
    codeNotAvailableBody: "Joining online isn't available for this team. Contact the league.",
    codeFullBody: 'This registration is full.',
    codeUnavailableBody: 'Contact the league to join this team.',
    /** Step 38: the team's roster is full and the waitlist is on. */
    joinWaitlistNote: "This team's roster is full. You'll join its waitlist. Nobody is charged until a spot is offered to you.",
    /** Step 38: an individual invitation to a full registration with the waitlist on. */
    joinWaitlistNoteIndividual: "This registration is full. You'll join the waitlist. Nobody is charged until a spot is offered to you.",
    waitlistedTitle: "You're on the waitlist",
  },
}

export default en
